import axios from 'axios';
import API_CONFIG from '../config/api.config';
import { getAuthHeader } from '../services/authService';

// Remove the extra 'api' from the URL
const CREDIT_API_URL = `${API_CONFIG.BASE_URL}/customer-credit`;

const handleApiError = (error) => {
    // For all errors, just pass through the error message
    throw error.response?.data?.message || error.message || 'An error occurred';
};

const creditService = {
    // Create a credit transaction
    createTransaction: async (transactionData) => {
        try {
            const headers = getAuthHeader();
            const response = await axios.post(`${CREDIT_API_URL}/transactions`, transactionData, {
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    // Get customer transactions
    getCustomerTransactions: async (customerId) => {
        try {
            const headers = getAuthHeader();
            const response = await axios.get(`${CREDIT_API_URL}/transactions/${customerId}`, {
                headers
            });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    // Get customer credit summary
    getCustomerCreditSummary: async (customerId) => {
        try {
            const headers = getAuthHeader();
            const response = await axios.get(`${CREDIT_API_URL}/summary/${customerId}`, {
                headers,
                params: {
                    includeSalesOrders: true  // Request to include credit sales from sales_orders table
                }
            });
            
            const data = response.data;
            return {
                totalCreditSales: data.totalCreditSales || 0,  // Total from sales_orders where payment_method = 'CREDIT'
                totalPayments: data.totalPayments || 0,        // Total from payment records
                totalReturns: data.totalReturns || 0,          // Total returns
                openingBalance: data.openingBalance || 0,      // Opening balance
                balanceDue: data.balanceDue || 0,              // Calculated balance due (totalCreditSales - totalPayments - totalReturns)
                currentBalance: data.currentBalance || 0       // Current credit balance
            };
        } catch (error) {
            throw handleApiError(error);
        }
    },

    // Process payment
    processPayment: async (customerId, paymentData) => {
        try {
            const headers = getAuthHeader();
            const response = await axios.post(`${CREDIT_API_URL}/${customerId}/payment`, paymentData, {
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    // Process return
    processReturn: async (customerId, returnData) => {
        try {
            const headers = getAuthHeader();
            const response = await axios.post(`${CREDIT_API_URL}/${customerId}/return`, returnData, {
                headers: {
                    ...headers,
                    'Content-Type': 'application/json'
                }
            });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    // Recalculate running balances for a customer
    recalculateRunningBalances: async (customerId) => {
        try {
            const headers = getAuthHeader();
            const response = await axios.post(`${CREDIT_API_URL}/${customerId}/recalculate`, {}, {
                headers
            });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    // Migrate existing credit sales to credit transaction system
    migrateExistingCreditSales: async (customerId) => {
        try {
            const headers = getAuthHeader();
            const response = await axios.post(`${CREDIT_API_URL}/${customerId}/migrate`, {}, {
                headers
            });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    },

    // Get all customers who have ever had credit transactions
    getAllCustomersWithCreditHistory: async () => {
        try {
            const headers = getAuthHeader();
            const response = await axios.get(`${CREDIT_API_URL}/all-customers-with-history`, {
                headers
            });
            return response.data;
        } catch (error) {
            throw handleApiError(error);
        }
    }
};

export default creditService; 